import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'connections/AuthContext';
import api from 'connections/api';
import routes_api from 'connections/routes_api';
import routes_web from 'connections/routes_web';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, Cog6ToothIcon, AdjustmentsHorizontalIcon, TagIcon } from '@heroicons/react/24/solid';
import DropdownCustom from './DropdownCustom';
import DropdownCurrency from './DropdownCurrency';

const FinanceSetupBanner = ({ onComplete }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    app_initial: '',
    default_currency: 'BRL',
    category_type: '',
    category_language: '',
  });
  const [loading, setLoading] = useState(false);
  const [appInitialOptions, setAppInitialOptions] = useState([]);

  // ##### EFFECTS
  useEffect(() => {
    // Build app initial options from user preferences
    if (user?.app_preferences) {
      const enabledModules = user.app_preferences.filter(
        pref => pref.app_enabled && pref.app_visible && pref.app_key !== 'accounts'
      );
      
      const options = [
        { value: 'ecosystem', label: 'Default (Ecosystem)' },
        ...enabledModules
          .sort((a, b) => a.app_order - b.app_order)
          .map(pref => ({
            value: pref.app_key,
            label: pref.app_key.charAt(0).toUpperCase() + pref.app_key.slice(1)
          }))
      ];
      
      setAppInitialOptions(options);
    }
  }, [user]);

  // ##### OPTIONS
  const categoryTypeOptions = [
    { value: 'standard', label: 'Standard' },
    { value: 'simple', label: 'Simple' },
  ];

  const languageOptions = [
    { value: 'EN', label: 'English' },
    { value: 'BR', label: 'Portuguese (BR)' },
  ];

  // ##### VALIDATION
  const isFormValid = () => {
    return formData.default_currency && 
           formData.category_type && 
           formData.category_language;
  };

  // ##### SUBMIT
  const handleSubmit = async () => {
    if (!isFormValid()) return;
    
    setLoading(true);
    try {
      // 1. Setup finance (profile + categories)
      const category_template = `${formData.category_type}_${formData.category_language}`;
      
      const res = await api.post(routes_api.finance_setup, {
        default_currency: formData.default_currency,
        category_template: category_template,
      });
      
      // 2. Update user app_initial (if selected)
      if (formData.app_initial === 'finance') {
        await api.patch(routes_api.accounts_update, {
          app_initial: 'finance'
        });
      }
      
      toast.success(`Finance setup complete!`);
      onComplete();
      
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error in finance setup';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ##### RENDER
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-20 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow max-w-lg w-full p-4 max-h-screen overflow-y-auto relative">
        
        {/* Back Button */}
        <button
          onClick={() => navigate(routes_web.accounts_home)}
          className="absolute top-4 left-4 p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          aria-label="Back to Accounts"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="mb-4 text-center">
          <h2 className="text-xl font-semibold text-green-800">First time?!</h2>
          <p className="text-xs text-gray-600 mt-1">Set your finance profile to get started</p>
        </div>

        {/* Sections */}
        <div>
          
          {/* Section 1: Basic Definitions */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="h-4 w-4 text-gray-600" />
              <span>Basic Definitions</span>
            </h3>
            
            {/* Grid with 2 cards side by side */}
            <div className="grid grid-cols-2 gap-2">
              {/* App on Initialization Card */}
              <div className="bg-gray-100 rounded-lg p-3">
                <label className="block text-xs text-gray-600 font-medium mb-2">
                  App on Initialization
                </label>
                <DropdownCustom
                  options={appInitialOptions}
                  selectedValue={formData.app_initial}
                  onSelect={(val) => setFormData({...formData, app_initial: val.value || val})}
                  placeholder="(Keep current)"
                  showAllOption={false}
                />
                <p className="text-xs text-gray-500 mt-2">
                  We suggest Finance for default open!
                </p>
              </div>

              {/* Default Currency Card */}
              <div className="bg-gray-100 rounded-lg p-3">
                <label className="block text-xs text-gray-600 font-medium mb-2">
                  Default Currency <span className="text-red-500">*</span>
                </label>
                <DropdownCurrency
                  selectedValue={formData.default_currency}
                  onSelect={(val) => setFormData({...formData, default_currency: val})}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Your main currency for transactions
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Categories */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <TagIcon className="h-4 w-4 text-gray-600" />
              <span>Categories</span>
            </h3>

            {/* Categories Card */}
            <div className="bg-gray-100 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                Categorization are made in different levels<br/>
                <span className="ml-2"><strong>Group:</strong> distinct theme (e.g. daily life, investment)</span><br/>
                <span className="ml-2"><strong>Category/sub-category:</strong> usual segmentation</span>
              </p>

              {/* Category Type and Language - Side by Side */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                {/* Category Type */}
                <DropdownCustom
                  options={categoryTypeOptions}
                  selectedValue={formData.category_type}
                  onSelect={(val) => setFormData({...formData, category_type: val.value || val})}
                  placeholder="Category options"
                  showAllOption={false}
                />

                {/* Language */}
                <DropdownCustom
                  options={languageOptions}
                  selectedValue={formData.category_language}
                  onSelect={(val) => setFormData({...formData, category_language: val.value || val})}
                  placeholder="Language"
                  showAllOption={false}
                />
              </div>

              {/* Legend Below - Only shows when category_type is selected */}
              {formData.category_type && (
                <div className="text-xs text-gray-500 leading-relaxed ml-2 mt-2">
                  {formData.category_type === 'simple' && (
                    <p><strong>Simple:</strong> uses only category</p>
                  )}
                  {formData.category_type === 'standard' && (
                    <p><strong>Standard:</strong> uses category and sub-category ; includes essential/recurring segmentation</p>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Helper Text */}
        <p className="text-center text-xs text-gray-500 mt-6 flex items-center justify-center gap-1">
          <span>All can be changed later in Settings</span>
          <Cog6ToothIcon className="h-3.5 w-3.5 text-gray-500" />
        </p>

        {/* Action Button */}
        <button
          onClick={handleSubmit}
          disabled={!isFormValid() || loading}
          className={`
            w-full mt-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
            ${isFormValid() && !loading
              ? 'bg-green-700 hover:bg-green-800 text-white cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }
          `}
        >
          {loading ? "Setting up..." : "I'm Ready!"}
        </button>
      </div>
    </div>
  );
};

export default FinanceSetupBanner;

